import { LeaveAreaCode } from '@gb-ticket/shared-types';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { ListPagination } from '@/components/list-pagination';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { PersonChip } from '@/platform/components/person-chip';
import { leavePanelClassName } from '@/products/leave/features/leave-panel';
import { formatOvertimeHours } from '@/products/leave/features/leave-home';
import { formatLeaveDays } from '@/products/leave/features/preview-vacation-days';
import { formatLeaveRange } from '@/products/leave/features/leave-labels';
import { leaveAreaEnabled, useLeaveAreas } from '@/products/leave/features/use-leave-areas';
import { useDecideLeaveOvertime, useLeaveInbox } from '@/products/leave/features/use-leave';
import { leaveRequestDetailPath } from '@/products/leave/leave-paths';

export function LeaveInboxPage() {
  usePageTitle('Eingang');
  const [page, setPage] = useState(1);
  const inbox = useLeaveInbox(page);
  const areas = useLeaveAreas();
  const overtimeOn = leaveAreaEnabled(areas.data, LeaveAreaCode.OVERTIME) !== false;
  const capacityOn = leaveAreaEnabled(areas.data, LeaveAreaCode.CAPACITY) !== false;
  const items = (inbox.data?.items ?? []).filter(
    (item) => overtimeOn || item.entryKind !== 'overtime',
  );

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Eingang"
        description={overtimeOn ? 'Offene Anträge und Überstunden' : 'Offene Anträge'}
      />
      {inbox.isPending ? (
        <p className="text-sm text-muted-foreground">Eingang wird geladen …</p>
      ) : null}
      {inbox.isError ? (
        <p className="text-sm text-destructive">
          {inbox.error instanceof ApiRequestError
            ? inbox.error.message
            : 'Der Eingang konnte nicht geladen werden.'}
        </p>
      ) : null}
      {inbox.data && items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Keine offenen Einträge.</p>
      ) : null}
      <ul className="space-y-3">
        {items.map((item) =>
          item.entryKind === 'request' ? (
            <li key={item.id} className={leavePanelClassName}>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <PersonChip
                  className="font-medium"
                  displayName={item.requester.displayName}
                  profilePhotoUrl={item.requester.profilePhotoUrl}
                />
                <p className="text-sm text-muted-foreground">{stepLabel(item)}</p>
              </div>
              <p className="mt-1 text-sm">{item.absenceType.labelDe}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {formatLeaveRange(item.startsOn, item.endsOn)} · {formatLeaveDays(item.days)} Tage
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                Vertretung: {item.substitute?.displayName ?? 'keine'}
              </p>
              {item.warnings?.substitutionOtherDepartment ? (
                <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">
                  Hinweis: Die Vertretung gehört zu einer anderen Abteilung.
                </p>
              ) : null}
              {capacityOn
                ? item.warnings?.capacityUnderMin?.map((warning) => (
                    <p
                      key={warning.date}
                      className="mt-2 text-sm text-amber-700 dark:text-amber-300"
                    >
                      Besetzungswarnung am{' '}
                      {new Intl.DateTimeFormat('de-DE').format(
                        new Date(`${warning.date}T12:00:00`),
                      )}
                      : nur {warning.present} von mindestens {warning.minPresent} Personen anwesend.
                    </p>
                  ))
                : null}
              <Link
                to={leaveRequestDetailPath(item.id)}
                className="mt-3 inline-flex text-sm font-medium text-primary hover:underline"
              >
                Antrag öffnen
              </Link>
            </li>
          ) : (
            <OvertimeInboxCard key={item.id} item={item} />
          ),
        )}
      </ul>
      {inbox.data ? (
        <ListPagination
          page={inbox.data.page}
          pageSize={inbox.data.pageSize}
          total={inbox.data.total}
          onPageChange={setPage}
        />
      ) : null}
    </div>
  );
}

function OvertimeInboxCard({
  item,
}: {
  item: {
    id: string;
    user: { displayName: string };
    hours: number;
    occurredOn: string;
    note: string | null;
    overtimeLimit: {
      projectedHours: number;
      maxOvertimeHours: number;
    } | null;
  };
}) {
  const decide = useDecideLeaveOvertime();
  const [decisionNote, setDecisionNote] = useState('');

  function fail(error: unknown, fallback: string) {
    toast.error(error instanceof ApiRequestError ? error.message : fallback);
  }

  return (
    <li className={leavePanelClassName}>
      <p className="font-medium">{item.user.displayName}</p>
      <p className="mt-1 text-sm">Überstunden</p>
      <p className="mt-1 text-sm text-muted-foreground">
        {new Intl.DateTimeFormat('de-DE').format(new Date(`${item.occurredOn}T12:00:00`))} ·{' '}
        {formatOvertimeHours(item.hours)}
      </p>
      {item.note ? <p className="mt-1 text-sm text-muted-foreground">{item.note}</p> : null}
      {item.overtimeLimit ? (
        <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">
          Hinweis: Das Überstundenkonto würde{' '}
          {formatOvertimeHours(item.overtimeLimit.projectedHours)} betragen. Die Grenze liegt bei{' '}
          {formatOvertimeHours(item.overtimeLimit.maxOvertimeHours)}.
        </p>
      ) : null}
      <div className="mt-3 space-y-3">
        <div>
          <Label htmlFor={`overtime-note-${item.id}`}>Begründung bei Ablehnung</Label>
          <textarea
            id={`overtime-note-${item.id}`}
            className="mt-2 min-h-16 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
            value={decisionNote}
            onChange={(event) => setDecisionNote(event.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            disabled={decide.isPending}
            onClick={() => {
              void decide
                .mutateAsync({ id: item.id, body: { decision: 'approved' } })
                .then(() => toast.success('Überstunden genehmigt.'))
                .catch((error: unknown) => fail(error, 'Genehmigen ist fehlgeschlagen.'));
            }}
          >
            Genehmigen
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={decide.isPending}
            onClick={() => {
              void decide
                .mutateAsync({
                  id: item.id,
                  body: { decision: 'rejected', decisionNote: decisionNote.trim() || null },
                })
                .then(() => toast.success('Überstunden abgelehnt.'))
                .catch((error: unknown) => fail(error, 'Ablehnen ist fehlgeschlagen.'));
            }}
          >
            Ablehnen
          </Button>
        </div>
      </div>
    </li>
  );
}

function stepLabel(item: { approvalStep: number; missingSkipManager?: boolean }): string {
  if (item.missingSkipManager) {
    return 'Zweiter Schritt ohne Vorgesetzte';
  }
  return item.approvalStep >= 2 ? 'Schritt 2' : 'Schritt 1';
}
